// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Grid, IconButton } from '@mui/material';
import { FadingTooltip } from '@cosmotech/ui';
import { GenericTextInput, GenericEnumInput, GenericMultiSelect } from '../..';

export const CustomItem = ({ fields, disabled, targetDatasetId, setRowValue, deleteRow, rowValue }) => {
  const { t } = useTranslation();

  const changeFieldValue = (fieldIndex, newValue) => {
    const newRowValue = [...rowValue];
    newRowValue[fieldIndex] = newValue;
    setRowValue(newRowValue);
  };

  const itemWidth = useMemo(() => (fields.length > 0 ? 100 / fields.length : 100), [fields.length]);

  const getGenericInput = (field, fieldIndex) => {
    const inputType = field.varType;
    const value = rowValue[fieldIndex];
    if (disabled === true) {
      field.options = { readOnly: disabled };
    }
    if (inputType === 'string') {
      return (
        <GenericTextInput
          parameterData={field}
          context={{ editMode: true }}
          parameterValue={value}
          setParameterValue={(newValue) => changeFieldValue(fieldIndex, newValue)}
          gridItemProps={{ xs: 12 }}
          isDirty={null}
          error={null}
        />
      );
    } else if (inputType === 'enum') {
      return (
        <GenericEnumInput
          parameterData={field}
          context={{ editMode: true, targetDatasetId }}
          parameterValue={value}
          setParameterValue={(newValue) => changeFieldValue(fieldIndex, newValue)}
          resetParameterValue={() => field?.defaultValue}
          gridItemProps={{ xs: 12 }}
          isDirty={null}
          error={null}
        />
      );
    } else if (inputType === 'list') {
      return (
        <GenericMultiSelect
          gridItemProps={{ xs: 3 }}
          parameterData={field}
          context={{ editMode: true, targetDatasetId }}
          parameterValue={value}
          setParameterValue={(newValue) => changeFieldValue(fieldIndex, newValue)}
          isDirty={null}
          error={null}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Grid item container direction="row" spacing={1} sx={{ p: 1, justifyContent: 'space-between' }} wrap="nowrap">
      <Grid item container spacing={1}>
        {fields.map((field, fieldIndex) => (
          <Grid item key={fieldIndex} sx={{ width: itemWidth + '%' }}>
            {getGenericInput(field, fieldIndex)}
          </Grid>
        ))}
      </Grid>
      <Grid item>
        <FadingTooltip title={t('genericcomponent.itemList.button.deleteRow', 'Delete row')} disableInteractive={true}>
          <IconButton disabled={disabled} variant="contained" color="primary" onClick={deleteRow} sx={{ px: 0 }}>
            <DeleteForeverIcon />
          </IconButton>
        </FadingTooltip>
      </Grid>
    </Grid>
  );
};

CustomItem.propTypes = {
  disabled: PropTypes.bool,
  targetDatasetId: PropTypes.string,
  fields: PropTypes.array.isRequired,
  setRowValue: PropTypes.func.isRequired,
  deleteRow: PropTypes.func.isRequired,
  rowValue: PropTypes.array,
};

CustomItem.defaultProps = {
  disabled: false,
  rowValue: [],
};
